import { CheckIcon, CopyIcon, Spinner, Typography } from "@codat/orchard-ui";
import { useSubmitMessageFeedback } from "hooks/query/useSubmitMessageFeedback";
import React, { useState } from "react";
import { classNames } from "react-cmdk";
import ReactMarkdown from "react-markdown";
import { formatDateAndTimeWithShortString } from "utils/formatDate";
import { isTemporaryMessageId } from "utils/messageUtils";

import { DoubleThumbsUpIcon, ThumbsDownIcon, ThumbsUpIcon } from "./icons";
import styles from "./Message.module.scss";
import { CodatLogo } from ".";

interface MessageProps {
  createdAt: string;
  content: string;
  role: "assistant" | "user";
  isThinking?: boolean;
  isStreaming?: boolean;
  mostRecentMessageRef?: React.RefObject<HTMLDivElement> | null;
  id: string;
  companyId: string;
  threadId: string;
  feedbackScore?: number | null;
}

const FeedbackButton = ({
  icon: Icon,
  label,
  isActive,
  onClick,
  dataTestId,
}: {
  icon: React.ComponentType<{ className?: string; isActive?: boolean }>;
  label: string;
  isActive: boolean;
  onClick: () => void;
  dataTestId: string;
}) => (
  <button
    className={classNames(styles.feedbackButton, isActive && styles.active)}
    onClick={onClick}
    aria-label={`${label}${isActive ? " (selected)" : ""}`}
    aria-pressed={isActive}
    data-testid={dataTestId}
  >
    <Icon className={styles.feedbackIcon} isActive={isActive} />
  </button>
);

const CopyButton = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <button
      className={styles.copyButton}
      onClick={handleCopy}
      aria-label="Copy message"
      data-testid="copy-button"
    >
      <Typography
        as="span"
        variant="body_sm"
        noMargin
        className={styles.copiedState}
      >
        {copied ? (
          <>
            <CheckIcon />
            <span>Copied</span>
          </>
        ) : (
          <>
            <CopyIcon />
            <span>Copy</span>
          </>
        )}
      </Typography>
    </button>
  );
};

const Message = ({
  createdAt,
  content,
  role,
  isThinking,
  isStreaming,
  mostRecentMessageRef,
  id: messageId,
  companyId,
  threadId,
  feedbackScore,
}: MessageProps) => {
  const { mutate: submitFeedback } = useSubmitMessageFeedback();
  const [feedback, setFeedback] = useState<number | null>(
    feedbackScore ?? null
  );

  const handleFeedback = (newFeedback: 1 | 2 | 3) => {
    if (!messageId || isTemporaryMessageId(messageId)) return;
    setFeedback(newFeedback);
    submitFeedback({
      companyId,
      threadId,
      messageId,
      feedbackScore: newFeedback,
    });
  };

  const classes = classNames(
    styles.message,
    role === "assistant" ? styles.assistant : styles.user
  );

  if (role === "assistant") {
    return (
      <article className={classes} data-testid="assistant-message">
        <div className={styles.logo}>
          <CodatLogo />
        </div>
        <div>
          {isThinking ? (
            <Typography
              className={styles.thinking}
              as="div"
              variant="heading_sm"
              noMargin
            >
              <span>Thinking</span>
              <Spinner className={styles.thinkingSpinner} />
            </Typography>
          ) : (
            <>
              <div className={styles.header}>
                <Typography
                  as="div"
                  variant="heading_xs"
                  className={styles.createdAt}
                  noMargin
                >
                  {formatDateAndTimeWithShortString(createdAt, true)}
                </Typography>
              </div>
              <Typography as="div" variant="body_md" noMargin>
                <ReactMarkdown className={styles.content}>
                  {content}
                </ReactMarkdown>
              </Typography>
              {!isStreaming && (
                <div className={styles.buttonWrapper}>
                  <CopyButton value={content} />
                  <div className={styles.feedbackButtons}>
                    <FeedbackButton
                      icon={ThumbsDownIcon}
                      label="Mark as not helpful"
                      isActive={feedback === 1}
                      onClick={() => handleFeedback(1)}
                      dataTestId="feedback-button-not-helpful"
                    />
                    <FeedbackButton
                      icon={ThumbsUpIcon}
                      label="Mark as helpful"
                      isActive={feedback === 2}
                      onClick={() => handleFeedback(2)}
                      dataTestId="feedback-button-helpful"
                    />
                    <FeedbackButton
                      icon={DoubleThumbsUpIcon}
                      label="Mark as very helpful"
                      isActive={feedback === 3}
                      onClick={() => handleFeedback(3)}
                      dataTestId="feedback-button-very-helpful"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </article>
    );
  }

  return (
    <article
      className={classes}
      data-testid="user-message"
      ref={mostRecentMessageRef ? mostRecentMessageRef : undefined}
    >
      <Typography as="div" variant="body_md" noMargin>
        <ReactMarkdown className={styles.content}>{content}</ReactMarkdown>
      </Typography>
    </article>
  );
};

export default Message;
